import type { Download, OptionDB } from "@project/emerge";
import { AbstractProductDefinition } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import type { PriceData } from "@project/pricing";
import groupBy from "lodash/groupBy";
import type { Group, Vector3 } from "three";
import { GroupAnimationManager } from "../animations/GroupAnimationManager";
import { AddableBuildStep } from "../buildsteps/AddableBuildStep";
import { AngleSectionBuildStep } from "../buildsteps/AngleSectionBuildStep";
import { CaseInteriorBuildStep } from "../buildsteps/CaseInteriorBuildStep";
import { CornerBraceBuildStep } from "../buildsteps/CornerBraceBuildStep";
import { CornerBuildStep } from "../buildsteps/CornerBuildStep";
import { CoverPaddingBuildStep } from "../buildsteps/CoverPaddingBuildStep";
import { FlightCaseBlueprintCalculator } from "../buildsteps/FlightCaseBlueprintCalculator";
import { OuterPanelBuildStep } from "../buildsteps/OuterPanelBuildStep";
import { ProfileBuildStep } from "../buildsteps/ProfileBuildStep";
import { Rack19InteriorBuildStep } from "../buildsteps/Rack19InteriorBuildStep";
import { RackInteriorBuildStep } from "../buildsteps/RackInteriorBuildStep";
import { RollerBuildStep } from "../buildsteps/RollerBuildStep";
import { RunnerBuildStep } from "../buildsteps/RunnerBuildStep";
import { PadDownloadGenerator } from "../downloads/PadDownload";
import { PanelDownloadGenerator } from "../downloads/PanelDownload";
import { PartsListDownload } from "../downloads/PartsListDownload";
import { ProfileDownloadGenerator } from "../downloads/ProfileDownload";
import { AddableType } from "../enum/AddableType";
import { ConnectorCategory } from "../enum/ConnectorType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { LTProducts } from "../enum/LTProducts";
import { LTPartType } from "../enum/PartType";
import { ROLLER_TYPE } from "../enum/ROLLER_TYPE";
import { VariableOptionType } from "../enum/VariableOptionType";
import { SupplyPartPanelInteraction } from "../interactions/SupplyPartPanelInteraction";
import type { CutProperties } from "../interfaces/CutProperties";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { OuterPanelProperties } from "../interfaces/OuterPanelProperties";
import type { PadDefinition } from "../interfaces/PadProperties";
import type { ProfileProperties } from "../interfaces/ProfileProperties";
import type { AngleSectionDefinition } from "../parts/AngleSectionDefinition";
import { hydrateImportedConnectorPartSelections } from "../settings/hydrateImportedConnectorPartSelections";
import { hydrateImportedCornerPartSelections } from "../settings/hydrateImportedCornerPartSelections";
import { hydrateImportedCreeperRollers } from "../settings/hydrateImportedCreeperRollers";
import { hydrateImportedExteriorRollers } from "../settings/hydrateImportedExteriorRollers";
import { createAddableGrid } from "../structure/AddableInfos";
import { createConnectorSet } from "../structure/ConnectorSetInfos";
import { createRollerBoard } from "../structure/RollerBoardInfos";
import { createRunners } from "../structure/RunnerInfos";
import type { VariableOptions } from "../types/VariableOptions";
import { CURRENT_PRODUCT_SETTINGS_VERSION } from "../user_options/constants";
import { createCaseInterior } from "../user_options/InteriorOptions";
import type { RollerBoardTypes } from "../user_options/RollerType";

// Simple interface for animation state
interface GroupAnimationState {
  isOpen: boolean;
  isAnimating: boolean;
}

export class FlightCaseDefinition extends AbstractProductDefinition<
  FlightCaseSettings,
  FlightCaseBlueprint,
  // biome-ignore lint/suspicious/noExplicitAny: buildsteps emit heterogeneous part defs; not all satisfy AbstractPartDefinition (e.g. PianoHingeDefinition).
  any
> {
  private blueprintCalculator = new FlightCaseBlueprintCalculator();
  private groupAnimationManager = new GroupAnimationManager();

  constructor(settings: FlightCaseSettings, optionDb: OptionDB) {
    // Hydrators replace legacy/partial slices (corners, rollers, connectors)
    // in-place. Callers often pass a frozen draft (react-hook-form / immer /
    // DB response + deep-freeze in dev), so mutating the original settings
    // throws "… is read-only". Work on a structured clone so hydration is
    // always safe; the product owns its own settings copy anyway.
    const ownedSettings = structuredClone(settings) as FlightCaseSettings;
    hydrateImportedCreeperRollers(ownedSettings, optionDb);
    hydrateImportedExteriorRollers(ownedSettings, optionDb);
    hydrateImportedConnectorPartSelections(ownedSettings, optionDb);
    hydrateImportedCornerPartSelections(ownedSettings, optionDb);
    super(ownedSettings);

    // Initialize buildsteps
    this.buildSteps = [
      new OuterPanelBuildStep(),
      new ProfileBuildStep(),
      new AngleSectionBuildStep(),
      new CornerBraceBuildStep(),
      new CornerBuildStep(),
      new RollerBuildStep(),
      new RunnerBuildStep(),
      // Interior buildsteps - each handles one interior type and self-filters
      new CaseInteriorBuildStep(), // CASE: volumes, tray panels, pads
      new RackInteriorBuildStep(), // RACK: tray panel dividers
      new Rack19InteriorBuildStep(), // RACK19: rack rails, underlay panels
      // Cover padding - handles all interior types
      new CoverPaddingBuildStep(),
      new AddableBuildStep(),
    ];

    // Initialize part interactions
    this.partInteractions = [
      new SupplyPartPanelInteraction(), // Cutouts von SupplyParts auf Außenplatten und Polster (Pads)
    ];
  }

  protected generateBlueprint(
    settings: FlightCaseSettings,
  ): FlightCaseBlueprint {
    return this.blueprintCalculator.execute(settings);
  }
  protected updateBlueprint(
    settings: FlightCaseSettings,
    _blueprint: FlightCaseBlueprint,
    _changes: Partial<FlightCaseSettings>,
  ): FlightCaseBlueprint {
    return this.blueprintCalculator.execute(settings);
  }

  /**
   * Returns the variable options available for this product.
   */
  public getAllowedVariableOptions(
    optionDb: OptionDB,
    cuts: CutProperties[],
  ): {
    [key in keyof typeof VariableOptionType]: () => VariableOptions;
  } {
    return FlightCaseDefinition.getAllowedVariableOptions(optionDb, cuts);
  }

  /**
   * A static helper to provide default settings
   */
  public static getSerializedDefaultVersion(
    optionDb: OptionDB,
    settingsOverride: Partial<FlightCaseSettings> & object = {},
  ): FlightCaseSettings {
    return {
      ...settingsOverride,
      coverPaddings: {},
      cuts: [],
      exteriorOptions: {
        angleSections: optionDb.getByName(
          LTCaseUserOption.AngleSectionType,
          "M1378",
        ),
        color: optionDb.getByName(LTCaseUserOption.PanelColor, "Phenol"),
        cornerBraces: optionDb.getByName(
          LTCaseUserOption.CornerBraceType,
          "AH4043A",
        ),
        cornerBracesCover: optionDb.getByName(
          LTCaseUserOption.CornerBraceType,
          "AH4043A",
        ),
        cornersBottom: optionDb.getByName(
          LTCaseUserOption.CornerType,
          "AH41201LT",
        ),
        cornersTop: optionDb.getByName(
          LTCaseUserOption.CornerType,
          "AH41201LT",
        ),
        profiles: optionDb.getByName(LTCaseUserOption.ProfileType, "M6102"),
        rollers: (optionDb
          .getOptions(LTCaseUserOption.RollerType)
          .find(
            (r) =>
              (r as { rollerType?: number }).rollerType === ROLLER_TYPE.NONE,
          ) ??
          optionDb.getByName(
            LTCaseUserOption.RollerType,
            "Keine",
          )) as RollerBoardTypes,
        strength: 6.5,
      },
      interiorOptions: createCaseInterior(optionDb),
      options: {} as Record<string, unknown>,
      type: LTProducts.Haube,
      variableOptions: [],
      version: CURRENT_PRODUCT_SETTINGS_VERSION,
    };
  }

  public static getAllowedVariableOptions(
    optionDb: OptionDB,
    cuts: CutProperties[],
  ): {
    [key in keyof typeof VariableOptionType]: () => VariableOptions;
  } {
    return {
      [VariableOptionType.LOCK_SET]: () =>
        createConnectorSet(
          optionDb,
          ConnectorCategory.Lock,
          cuts[0]?.groupName || "",
          Sides.LEFT,
          "AH17291S",
        ),
      [VariableOptionType.HINGE_SET]: () =>
        createConnectorSet(
          optionDb,
          ConnectorCategory.Hinge,
          cuts[0]?.groupName || "",
          Sides.LEFT,
          "AH2524",
        ),
      [VariableOptionType.FEET]: () =>
        createAddableGrid(optionDb, AddableType.Foot, Sides.BOTTOM, "AH4904", {
          horizontalCount: 2,
          verticalCount: 2,
        }),
      [VariableOptionType.HANDLES]: () =>
        createAddableGrid(optionDb, AddableType.Handle, Sides.FRONT, "AH3415"),
      [VariableOptionType.DISHES]: () =>
        createAddableGrid(optionDb, AddableType.Dish, Sides.TOP, "AH88001D"),
      [VariableOptionType.TROLLEY_HANDLES]: () =>
        createAddableGrid(
          optionDb,
          AddableType.TrolleyHandle,
          Sides.BOTTOM,
          "AH34703",
        ),
      [VariableOptionType.ROLLER_BOARD]: () => createRollerBoard(optionDb),
      [VariableOptionType.RUNNERS]: () => createRunners(),
    };
  }

  public async update(changes: Partial<FlightCaseSettings>): Promise<void> {
    // Update settings with changes
    this.settings = { ...this.settings, ...changes };

    // Update blueprint using calculator's update method
    if (this.blueprint) {
      this.blueprint = this.blueprintCalculator.update(
        this.settings,
        this.blueprint,
        this.settings,
      );
    } else {
      // If no previous blueprint exists, do a full calculation
      this.blueprint = this.blueprintCalculator.execute(this.settings);
    }

    // Rebuild parts with updated settings/blueprint
    await this.build();
  }

  /**
   * Serialize the product definition
   */
  public serialize(): FlightCaseSettings {
    return { ...this.settings };
  }

  /**
   * Override build method to calculate group animations with rotation points after build steps
   */
  public async build(): Promise<void> {
    // Call parent build method first
    await super.build();

    // Initialize group animations after blueprint is ready
    if (this.blueprint) {
      // Find all hinge definitions from the parts
      const hingeDefinitions: Array<{
        group?: string;
        position: Vector3;
        partSelection?: { hingeOffset?: number };
        side?: Sides;
      }> = [];

      for (const part of Array.from(this.partDefinitions.values())) {
        const hingePart = part as {
          type?: string;
          group?: string;
          position?: Vector3;
          partSelection?: unknown;
          side?: Sides;
          edgeSide?: Sides;
          segmentLength?: number;
        };
        if (
          hingePart.type === "Hinge" &&
          "group" in part &&
          "position" in part &&
          hingePart.segmentLength == null &&
          hingePart.position !== undefined
        ) {
          hingeDefinitions.push({
            group: hingePart.group,
            position: hingePart.position,
            partSelection: hingePart.partSelection as
              | { hingeOffset?: number }
              | undefined,
            side: hingePart.side ?? hingePart.edgeSide,
          });
        }
      }

      // Use updateConnectors if manager has groups, otherwise initialize
      const hasExistingGroups =
        this.groupAnimationManager.getAvailableGroupNames().length > 0;

      if (hasExistingGroups) {
        // Update existing animations while preserving states
        this.groupAnimationManager.updateConnectors(
          this.blueprint.connectors,
          this.settings.cuts,
          hingeDefinitions,
          this.blueprint.innerDimensions,
        );
      } else {
        // First-time initialization
        this.groupAnimationManager.initialize(
          this.blueprint.connectors,
          this.settings.cuts,
          hingeDefinitions,
          this.blueprint.innerDimensions,
        );
      }

      // Calculate complete group animations with rotation points
      this.blueprintCalculator.calculateGroupAnimationsWithRotationPoints(
        this.blueprint,
        hingeDefinitions,
        this.settings,
      );
    }
  }

  /**
   * Animation Interface Methods - Diese werden vom ProductViewer verwendet
   */

  /**
   * Registriert eine Gruppe für Animationen
   */
  public registerAnimationGroup(groupName: string, groupRef: Group): boolean {
    return this.groupAnimationManager.registerGroup(groupName, groupRef);
  }

  /**
   * Schaltet den Zustand einer Gruppe um
   */
  public toggleAnimationGroup(groupName: string): boolean {
    return this.groupAnimationManager.toggleGroup(groupName);
  }

  /**
   * Prüft ob eine Gruppe interaktiv ist
   */
  public isGroupInteractive(groupName: string): boolean {
    return this.groupAnimationManager.isGroupInteractive(groupName);
  }

  /**
   * Prüft ob eine Gruppe registriert ist
   */
  public isGroupRegistered(groupName: string): boolean {
    return this.groupAnimationManager.isGroupRegistered(groupName);
  }

  /**
   * Gibt den Zustand einer Gruppe zurück
   */
  public getGroupAnimationState(
    groupName: string,
  ): GroupAnimationState | undefined {
    return this.groupAnimationManager.getGroupState(groupName);
  }

  /**
   * Gibt alle Gruppenzustände zurück
   */
  public getAllGroupAnimationStates(): Map<string, GroupAnimationState> {
    return this.groupAnimationManager.getAllGroupStates();
  }

  /**
   * Gibt alle verfügbaren Gruppennamen zurück
   */
  public getAvailableAnimationGroupNames(): string[] {
    return this.groupAnimationManager.getAvailableGroupNames();
  }

  /**
   * Cleanup für Animationen
   */
  public disposeAnimations(): void {
    this.groupAnimationManager.dispose();
  }

  /**
   * Generate DXF downloads for this flight case product
   * Groups parts by material/type and creates DXF files for each group
   */
  public override async getDownloads(
    context?: Record<string, unknown>,
  ): Promise<Download[]> {
    const priceData = context?.priceData as PriceData | undefined;
    // Filter for panel parts
    const panelParts: OuterPanelProperties[] = Array.from(
      this.partDefinitions.values(),
    ).filter(
      (part): part is OuterPanelProperties =>
        part.type === LTPartType.Panel || part.type === LTPartType.TrayPanel,
    );

    // Filter for pad parts
    const padParts: PadDefinition[] = Array.from(
      this.partDefinitions.values(),
    ).filter((part): part is PadDefinition => part.type === LTPartType.Pad);

    // Filter for profile parts
    const profileParts: ProfileProperties[] = Array.from(
      this.partDefinitions.values(),
    ).filter(
      (part): part is ProfileProperties => part.type === LTPartType.Profile,
    );

    // Filter for angle section parts
    const angleSectionParts: AngleSectionDefinition[] = Array.from(
      this.partDefinitions.values(),
    ).filter(
      (part): part is AngleSectionDefinition =>
        part.type === LTPartType.AngleSection,
    );

    const downloads: (Download | null)[] = [];

    // Generate panel downloads grouped by material
    if (panelParts.length > 0) {
      const panelsByMaterial = groupBy(
        panelParts,
        (part) => `${part.color?.name || "Unknown"} ${part.strength}mm`,
      );

      const panelDownloads = await Promise.all(
        Object.entries(panelsByMaterial).map(async ([materialDesc, panels]) => {
          try {
            const generator = new PanelDownloadGenerator(panels, materialDesc);
            return await generator.generateDownload();
          } catch (error) {
            console.error(
              `Failed to generate panel download for ${materialDesc}:`,
              error,
            );
            return null;
          }
        }),
      );
      downloads.push(...panelDownloads);
    }

    // Generate pad downloads grouped by material
    if (padParts.length > 0) {
      const padsByMaterial = groupBy(
        padParts,
        (part) => `${part.partSelection?.name || "Unknown"} ${part.strength}mm`,
      );

      const padDownloads = await Promise.all(
        Object.entries(padsByMaterial).map(async ([materialDesc, pads]) => {
          try {
            const generator = new PadDownloadGenerator(pads, materialDesc);
            return await generator.generateDownload();
          } catch (error) {
            console.error(
              `Failed to generate pad download for ${materialDesc}:`,
              error,
            );
            return null;
          }
        }),
      );
      downloads.push(...padDownloads);
    }

    // Generate profile download
    if (profileParts.length > 0) {
      try {
        const modelName = `${this.settings.type.toUpperCase()} ${this.blueprint?.innerDimensions.x}x${this.blueprint?.innerDimensions.y}x${this.blueprint?.innerDimensions.z}`;
        const profileDownload = await new ProfileDownloadGenerator(
          profileParts,
          angleSectionParts,
          modelName,
        ).generateDownload();
        downloads.push(profileDownload);
      } catch (error) {
        console.error("Failed to generate profile download:", error);
      }
    }

    // Generate parts list (Stückliste) CSV
    try {
      const partsListDownload = new PartsListDownload(
        this.partDefinitions,
        priceData,
      );
      downloads.push(partsListDownload);
    } catch (error) {
      console.error("Failed to generate parts list download:", error);
    }

    return downloads.filter((d): d is Download => d !== null);
  }
}
